import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PricingConfig {
  currency: string;
  broker: { setup_fee_display: string; setup_fee_cents: number };
  seller: { monthly_display: string; monthly_cents: number };
  lead: { fee_display: string; fee_cents: number };
}

const fallbackConfig: PricingConfig = {
  currency: "aud",
  broker: { setup_fee_display: "AUD $89", setup_fee_cents: 8900 },
  seller: { monthly_display: "AUD $98", monthly_cents: 9800 },
  lead: { fee_display: "AUD $19", fee_cents: 1900 },
};

export function PricingSection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pricing, setPricing] = useState<PricingConfig>(fallbackConfig);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "pricing_config")
        .maybeSingle();
      if (data?.value) setPricing(data.value as unknown as PricingConfig);
    })();
  }, []);

  const plans = [
    {
      name: "Brokers",
      price: pricing.broker.setup_fee_display,
      period: "one-time setup",
      badge: null,
      description: "One-time setup, pay only for matched buyer leads.",
      features: [
        "Unlimited listings",
        "AI-powered buyer matching",
        `${pricing.lead.fee_display} per buyer lead`,
        "No ongoing subscription",
        "Lead management dashboard",
      ],
      leadPrice: `${pricing.lead.fee_display} per buyer lead`,
      cta: "Get Started as a Broker",
      action: "broker" as const,
      popular: true,
    },
    {
      name: "Private Sellers",
      price: pricing.seller.monthly_display,
      period: "per month, per listing",
      badge: null,
      description: "Monthly listing with targeted buyer introductions.",
      features: [
        "Single business listing",
        "AI-powered buyer matching",
        `${pricing.lead.fee_display} per buyer lead`,
        "Cancel anytime",
        "Lead management dashboard",
      ],
      leadPrice: `${pricing.lead.fee_display} per buyer lead`,
      cta: "List Your Business",
      action: "seller" as const,
      popular: false,
    },
    {
      name: "Multi-Listing / Enterprise",
      price: "Custom",
      period: "tailored quote",
      badge: "Best Value",
      description: "Bulk listings with discounted lead pricing and dedicated support.",
      features: [
        "Bulk listings",
        "Discounted lead pricing",
        "Dedicated account manager",
        "Custom onboarding",
        "Priority support",
      ],
      leadPrice: "Volume pricing available",
      cta: "Request a Quote",
      action: "enterprise" as const,
      popular: false,
    },
  ];

  const handlePlanClick = (action: "broker" | "seller" | "enterprise") => {
    if (action === "enterprise") {
      navigate("/contact");
      return;
    }
    navigate(user ? "/list-business" : "/sign-up");
  };

  return (
    <section id="pricing" className="py-20 bg-gradient-card">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            No commissions. No lock-in. Pay for real buyer interest.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <Card
              key={index}
              className={`relative group hover:shadow-large transition-all duration-300 hover:-translate-y-2 ${
                plan.popular ? "border-primary shadow-medium scale-105" : "border-border/50"
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge variant="secondary" className="bg-gradient-gold text-secondary-foreground font-semibold px-4 py-1">
                    <Star className="w-3 h-3 mr-1" />
                    {plan.badge}
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl font-bold text-foreground mb-2">
                  {plan.name}
                </CardTitle>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-primary">{plan.price}</span>
                  <span className="text-muted-foreground ml-2">{plan.period}</span>
                </div>
                <p className="text-muted-foreground text-sm">{plan.description}</p>
              </CardHeader>

              <CardContent className="pt-0">
                <div className="mb-6 p-3 bg-secondary/10 rounded-lg text-center">
                  <div className="text-sm font-medium text-secondary-foreground">Lead Pricing</div>
                  <div className="text-lg font-bold text-secondary">{plan.leadPrice}</div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={plan.popular ? "premium" : "hero"}
                  className="w-full"
                  size="lg"
                  onClick={() => handlePlanClick(plan.action)}
                >
                  {plan.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground">
            All plans include: Secure Stripe payments • Lead verification • No commissions • No lock-in
          </p>
        </div>
      </div>
    </section>
  );
}
